import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'motion/react'
import { Panel } from '../components/Panel'
import { Button } from '../components/Button'
import { Field } from '../components/Field'
import { api, type Segment, type SegmentPreview } from '../api/client'
import { fmtInt } from '../lib/format'
import {
  ARTICLE_FIELD_SPECS,
  FIELD_SPECS,
  LIST_OPS,
  NULLARY_OPS,
  OPS_BY_KIND,
  getArticleFieldSpec,
  getFieldSpec,
  type FieldSpec,
} from '../lib/segmentFields'

// ---------- Predicate model ----------

type Leaf = { field: string; op: string; value: string | number | boolean | string[] | null }
type Composite = { all: Node[] } | { any: Node[] }
type Engaged = {
  engaged: {
    event?: 'shown' | 'clicked'
    in_last?: string
    where?: Node
  }
}
type Node = Leaf | Composite | Engaged

const BLANK_DEF: Composite = { all: [] }
const BLANK_ENGAGED: Engaged = { engaged: { event: 'clicked', in_last: '90d' } }

// Field picker drives off FIELD_SPECS imported from ../lib/segmentFields.
// Op labels kept here; the list of ops shown in the op dropdown is filtered
// per field-kind via OPS_BY_KIND.
const OP_LABELS: Record<string, string> = {
  eq: '=',
  neq: '≠',
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
  in: 'IN',
  not_in: 'NOT IN',
  in_month: 'IN MONTH',
  contains: 'CONTAINS',
  starts_with: 'STARTS WITH',
  is_null: 'IS NULL',
  is_not_null: 'IS NOT NULL',
}

// ---------- Page ----------

export function SegmentBuilderPage() {
  const { id } = useParams<{ id: string }>()
  const isNew = id === 'new' || !id
  const nav = useNavigate()
  const qc = useQueryClient()

  const { data: existing } = useQuery({
    queryKey: ['segment', id],
    queryFn: () => api.get<Segment>(`/api/segments/${id}`),
    enabled: !isNew,
  })

  const [form, setForm] = useState({
    slug: '',
    name: '',
    description: '',
    mailgun_list_address: '',
    enabled: true,
    exclude_undeliverable: true,
  })
  const [def, setDef] = useState<Node>(BLANK_DEF)

  useEffect(() => {
    if (existing) {
      setForm({
        slug: existing.slug,
        name: existing.name,
        description: existing.description ?? '',
        mailgun_list_address: existing.mailgun_list_address ?? '',
        enabled: existing.enabled,
        exclude_undeliverable: existing.exclude_undeliverable ?? true,
      })
      setDef((existing.definition as Node) ?? BLANK_DEF)
    }
  }, [existing])

  // Live preview — debounce the definition into a preview query
  const [debouncedDef, setDebouncedDef] = useState(def)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedDef(def), 400)
    return () => clearTimeout(t)
  }, [def])

  // Don't preview an empty/unbuilt tree — saves a 400 round-trip on /new.
  const isPredicateReady = useMemo(() => {
    const n = debouncedDef as Node
    if ('all' in (n as Composite)) return (n as { all: Node[] }).all.length > 0
    if ('any' in (n as Composite)) return (n as { any: Node[] }).any.length > 0
    return true // leaf nodes are always "ready"
  }, [debouncedDef])

  const preview = useQuery({
    queryKey: ['preview', debouncedDef, form.exclude_undeliverable],
    queryFn: () =>
      api.post<SegmentPreview>('/api/segments/preview', {
        definition: debouncedDef,
        exclude_undeliverable: form.exclude_undeliverable,
      }),
    retry: false,
    enabled: isPredicateReady,
  })

  const save = useMutation({
    mutationFn: (payload: unknown) =>
      isNew
        ? api.post<Segment>('/api/segments', payload)
        : api.put<Segment>(`/api/segments/${id}`, payload),
    onSuccess: (seg) => {
      qc.invalidateQueries({ queryKey: ['segments'] })
      qc.invalidateQueries({ queryKey: ['segment', String(seg.id)] })
      nav(`/segments/${seg.id}`, { replace: true })
    },
  })

  return (
    <div className="p-8 space-y-6 max-w-[1400px]">
      <div className="flex items-center gap-3 mb-2">
        <Link to="/segments" className="label-amber hover:underline">← Segments</Link>
        <span className="label text-paper-trace">/</span>
        <span className="label">{isNew ? 'New' : form.slug}</span>
      </div>

      <header className="flex items-end justify-between border-b border-ink-500 pb-6">
        <div>
          <div className="label mb-1">{isNew ? 'Composing' : 'Editing'}</div>
          <h1 className="font-serif text-[42px] leading-[1.05] tracking-tight text-paper">
            {form.name || <span className="text-paper-faint italic">Untitled segment</span>}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => nav('/segments')}
          >
            Cancel
          </Button>
          <Button
            disabled={save.isPending}
            onClick={() =>
              save.mutate({
                ...form,
                mailgun_list_address: form.mailgun_list_address || null,
                definition: def,
              })
            }
          >
            {save.isPending ? 'Saving…' : isNew ? 'Save Segment' : 'Update'}
          </Button>
        </div>
      </header>

      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 space-y-6">
          <Panel label="Metadata" accent="amber">
            <div className="grid grid-cols-2 gap-5">
              <Field
                label="Slug"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value.replace(/[^a-z0-9_]/g, '') })}
                placeholder="active_flight_journal"
              />
              <Field
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Active Flight Journal Subscribers"
              />
              <Field
                label="Description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Optional description"
                className="col-span-2"
              />
              <Field
                label="Mailgun list address"
                value={form.mailgun_list_address}
                onChange={(e) => setForm({ ...form, mailgun_list_address: e.target.value })}
                placeholder="active@mg.flightjournal.com"
                className="col-span-2"
              />
              <label className="col-span-2 flex items-start gap-3 cursor-pointer select-none pt-1">
                <input
                  type="checkbox"
                  checked={form.exclude_undeliverable}
                  onChange={(e) => setForm({ ...form, exclude_undeliverable: e.target.checked })}
                  className="mt-1"
                />
                <span>
                  <span className="label block">Exclude undeliverable addresses</span>
                  <span className="font-mono text-[11px] text-paper-trace">
                    Filters out subscribers whose Mailgun validation marked them
                    as undeliverable, do_not_send, or high-risk. Matches the
                    dispatcher's skip rule so the live count equals the send
                    count. Leave on unless you specifically want to see the
                    full pre-validation cohort.
                  </span>
                </span>
              </label>
            </div>
          </Panel>

          <Panel label="Predicate Tree" accent="cyan">
            <PredicateNodeEditor node={def} onChange={setDef} depth={0} />
          </Panel>
        </div>

        <div className="col-span-12 lg:col-span-4 space-y-6">
          {/* The Hero: live readout */}
          <Panel label="Live Member Count" accent="amber" bodyClassName="p-0">
            <div className="scanlines relative overflow-hidden">
              <div className="p-8 pb-5 text-center">
                <div className="label mb-3">Matching subscribers</div>
                <div
                  className="font-mono font-bold text-[84px] leading-[0.95] text-amber-glow tabular-nums"
                  style={{ textShadow: '0 0 24px rgba(255, 182, 72, 0.4)' }}
                >
                  <AnimatePresence mode="popLayout">
                    <motion.span
                      key={preview.data?.count ?? 'loading'}
                      initial={{ opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 6 }}
                      transition={{ duration: 0.2 }}
                    >
                      {preview.isFetching
                        ? '···'
                        : preview.data
                          ? fmtInt(preview.data.count)
                          : preview.isError
                            ? 'ERR'
                            : '—'}
                    </motion.span>
                  </AnimatePresence>
                </div>
                <div className="font-mono text-[10px] text-paper-trace tabular-nums mt-3">
                  {preview.data
                    ? `QUERY RETURNED IN ${preview.data.ran_in_ms}ms`
                    : preview.isError
                      ? 'PREDICATE INVALID'
                      : 'WAITING…'}
                </div>
              </div>
              <div className="border-t border-ink-500 p-4">
                <div className="label mb-2">Sample members</div>
                {(preview.data?.sample ?? []).length === 0 ? (
                  <div className="label text-paper-trace">—</div>
                ) : (
                  <ul className="space-y-1">
                    {(preview.data?.sample ?? []).map((m, i) => (
                      <li key={i} className="font-mono text-[11px] text-paper-dim truncate">
                        {m.email}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </Panel>

          <Panel label="Predicate Source" accent="violet">
            <pre className="font-mono text-[11px] text-paper-dim leading-relaxed overflow-auto max-h-64">
{JSON.stringify(def, null, 2)}
            </pre>
          </Panel>
        </div>
      </div>
    </div>
  )
}

// ---------- Recursive predicate editor ----------

function PredicateNodeEditor({
  node, onChange, depth, fieldSpecs = FIELD_SPECS, allowEngaged = true,
}: {
  node: Node
  onChange: (n: Node) => void
  depth: number
  /** Which field set leaf editors should pick from. Default = subscriber graph;
   *  inside an `engaged.where` subtree we swap to ARTICLE_FIELD_SPECS. */
  fieldSpecs?: FieldSpec[]
  /** Whether composite editors at this level may add an `engaged` block.
   *  False inside an engaged.where subtree (no nesting engaged-in-engaged). */
  allowEngaged?: boolean
}) {
  if (isComposite(node)) {
    return (
      <CompositeEditor
        node={node}
        onChange={onChange}
        depth={depth}
        fieldSpecs={fieldSpecs}
        allowEngaged={allowEngaged}
      />
    )
  }
  if (isEngaged(node)) {
    return <EngagedEditor node={node} onChange={onChange} depth={depth} />
  }
  return <LeafEditor node={node} onChange={onChange} fieldSpecs={fieldSpecs} />
}

function CompositeEditor({
  node, onChange, depth, fieldSpecs, allowEngaged,
}: {
  node: Composite
  onChange: (n: Node) => void
  depth: number
  fieldSpecs: FieldSpec[]
  allowEngaged: boolean
}) {
  const kind = 'all' in node ? 'all' : 'any'
  const children = kind === 'all' ? (node as { all: Node[] }).all : (node as { any: Node[] }).any

  const setChildren = (next: Node[]) => {
    onChange(kind === 'all' ? { all: next } : { any: next })
  }

  const flipKind = () => {
    onChange(kind === 'all' ? { any: children } : { all: children })
  }

  const defaultLeaf: Leaf = fieldSpecs === FIELD_SPECS
    ? { field: 'subscription.status', op: 'eq', value: 'Active' }
    : { field: fieldSpecs[0].key, op: 'eq', value: defaultValueForKind(fieldSpecs[0], 'eq') }

  const addLeaf = () => {
    setChildren([...children, defaultLeaf])
  }
  const addGroup = () => {
    setChildren([...children, { all: [] }])
  }
  const addEngaged = () => {
    setChildren([...children, { engaged: { ...BLANK_ENGAGED.engaged } }])
  }

  return (
    <div
      className="relative border-l-2 pl-4 ml-2"
      style={{
        borderColor:
          depth === 0 ? '#ffb648' : depth === 1 ? '#5eeaff' : '#b69cff',
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={flipKind}
          className={`
            font-mono text-[10px] uppercase tracking-label px-2 py-0.5 border
            ${kind === 'all'
              ? 'text-amber-glow border-amber-glow/40 bg-amber-wash'
              : 'text-radar-cyan border-radar-cyan/40 bg-radar-cyan/5'}
          `}
          title="Toggle all / any"
        >
          {kind === 'all' ? 'ALL OF' : 'ANY OF'}
        </button>
        <span className="label text-paper-trace">
          ({children.length} {children.length === 1 ? 'predicate' : 'predicates'})
        </span>
      </div>

      <div className="space-y-2">
        {children.map((child, i) => (
          <div key={i} className="flex items-start gap-2 group">
            <div className="flex-1">
              <PredicateNodeEditor
                node={child}
                onChange={(next) => {
                  const copy = [...children]
                  copy[i] = next
                  setChildren(copy)
                }}
                depth={depth + 1}
                fieldSpecs={fieldSpecs}
                allowEngaged={allowEngaged}
              />
            </div>
            <button
              onClick={() => setChildren(children.filter((_, j) => j !== i))}
              className="opacity-0 group-hover:opacity-100 transition-opacity font-mono text-[10px] text-radar-red uppercase tracking-label px-2 py-1 hover:bg-radar-red/10"
              title="Remove"
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mt-3">
        <Button variant="ghost" size="sm" onClick={addLeaf}>+ predicate</Button>
        <Button variant="ghost" size="sm" onClick={addGroup}>+ group</Button>
        {allowEngaged && (
          <Button variant="ghost" size="sm" onClick={addEngaged}>+ engaged</Button>
        )}
      </div>
    </div>
  )
}

// ---------- Engaged editor ----------
//
// Renders an `engaged` block: event picker (shown|clicked), in_last text input
// (e.g. 30d, 6mo, 12h), and a recursive PredicateNodeEditor for `where` that
// uses ARTICLE_FIELD_SPECS so leaves there resolve to article.* fields. We
// disable `allowEngaged` inside `where` so we don't end up nesting engaged
// in engaged (semantically dubious, would compile to an EXISTS-in-EXISTS).
function EngagedEditor({
  node, onChange, depth,
}: {
  node: Engaged
  onChange: (n: Node) => void
  depth: number
}) {
  const spec = node.engaged
  const event = spec.event ?? 'shown'
  const inLast = spec.in_last ?? ''
  const where = spec.where

  const setEvent = (v: 'shown' | 'clicked') =>
    onChange({ engaged: { ...spec, event: v } })
  const setInLast = (v: string) => {
    const next = { ...spec }
    if (v.trim() === '') {
      delete next.in_last
    } else {
      next.in_last = v
    }
    onChange({ engaged: next })
  }
  const setWhere = (n: Node) =>
    onChange({ engaged: { ...spec, where: n } })

  const initWhere = () => {
    const first = ARTICLE_FIELD_SPECS[0]
    setWhere({ field: first.key, op: 'eq', value: defaultValueForKind(first, 'eq') })
  }
  const wrapInGroup = () => {
    if (!where) return
    setWhere({ all: [where] })
  }
  const clearWhere = () => {
    const next = { ...spec }
    delete next.where
    onChange({ engaged: next })
  }

  return (
    <div
      className="relative border-l-2 pl-4 ml-2 bg-radar-violet/5"
      style={{ borderColor: '#b69cff' }}
    >
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="font-mono text-[10px] uppercase tracking-label px-2 py-0.5 border border-radar-violet/40 text-radar-violet bg-radar-violet/5">
          ENGAGED
        </span>
        <select
          value={event}
          onChange={(e) => setEvent(e.target.value as 'shown' | 'clicked')}
          className="bg-ink-700 border border-ink-500 px-2 py-0.5 font-mono text-[12px] text-paper uppercase tracking-label cursor-pointer"
        >
          <option value="shown">SHOWN</option>
          <option value="clicked">CLICKED</option>
        </select>
        <span className="label text-paper-trace">in last</span>
        <input
          value={inLast}
          onChange={(e) => setInLast(e.target.value)}
          placeholder="30d  ·  6mo  ·  12h"
          className="bg-ink-700 border border-ink-500 px-2 py-0.5 font-mono text-[12px] text-paper w-[140px]"
        />
        <span className="label text-paper-trace">
          {depth === 0 ? '— top-level engagement filter' : ''}
        </span>
      </div>

      {where ? (
        <div className="space-y-2">
          <div className="label text-paper-trace">where article …</div>
          <div className="flex items-start gap-2 group">
            <div className="flex-1">
              <PredicateNodeEditor
                node={where}
                onChange={setWhere}
                depth={depth + 1}
                fieldSpecs={ARTICLE_FIELD_SPECS}
                allowEngaged={false}
              />
            </div>
            <button
              onClick={clearWhere}
              className="opacity-0 group-hover:opacity-100 transition-opacity font-mono text-[10px] text-radar-red uppercase tracking-label px-2 py-1 hover:bg-radar-red/10"
              title="Remove inner predicate"
            >
              ×
            </button>
          </div>
          {!isComposite(where) && (
            <div className="pt-1">
              <Button variant="ghost" size="sm" onClick={wrapInGroup}>
                + wrap in group
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={initWhere}>
            + article filter
          </Button>
          <span className="label text-paper-trace pt-1.5">
            no inner predicate — matches any tracked article
          </span>
        </div>
      )}
    </div>
  )
}

function LeafEditor({
  node, onChange, fieldSpecs = FIELD_SPECS,
}: {
  node: Leaf
  onChange: (n: Leaf) => void
  fieldSpecs?: FieldSpec[]
}) {
  const lookup = fieldSpecs === ARTICLE_FIELD_SPECS ? getArticleFieldSpec : getFieldSpec
  const spec = lookup(node.field) ?? fieldSpecs[0]
  const kind = spec.kind
  const opsForKind = OPS_BY_KIND[kind]

  const fieldOptions = useMemo(
    () => fieldSpecs.map((f) => ({ value: f.key, label: f.label })),
    [fieldSpecs],
  )

  const changeField = (newKey: string) => {
    const newSpec = lookup(newKey)
    if (!newSpec) return
    // If the current op isn't valid for the new kind, fall back to 'eq'.
    const nextOp = OPS_BY_KIND[newSpec.kind].includes(node.op) ? node.op : 'eq'
    // Reset value when the kind changes — carrying "Active" into a numeric
    // field is nonsensical.
    const nextValue =
      newSpec.kind === spec.kind ? node.value : defaultValueForKind(newSpec, nextOp)
    onChange({ ...node, field: newKey, op: nextOp, value: nextValue })
  }

  const changeOp = (nextOp: string) => {
    // Switching to a list op → ensure value is an array.
    // Switching off a list op → collapse array to first element.
    let nextValue: Leaf['value'] = node.value
    if (LIST_OPS.has(nextOp) && !Array.isArray(node.value)) {
      nextValue = node.value === null || node.value === undefined || node.value === ''
        ? []
        : [String(node.value)]
    } else if (!LIST_OPS.has(nextOp) && Array.isArray(node.value)) {
      nextValue = node.value.length > 0 ? node.value[0] : ''
    }
    // Switching to is_null/is_not_null → clear value.
    if (NULLARY_OPS.has(nextOp)) nextValue = null
    onChange({ ...node, op: nextOp, value: nextValue })
  }

  return (
    <div className="flex flex-wrap items-center gap-2 font-mono text-[12px] bg-ink-700/50 px-3 py-2 border border-ink-500">
      <Pill>
        <select
          value={node.field}
          onChange={(e) => changeField(e.target.value)}
          className="bg-transparent text-paper border-none outline-none font-mono text-[12px] cursor-pointer"
        >
          {fieldOptions.map((o) => (
            <option key={o.value} value={o.value} className="bg-ink-700 text-paper">
              {o.label}
            </option>
          ))}
        </select>
      </Pill>
      <Pill tone="amber">
        <select
          value={node.op}
          onChange={(e) => changeOp(e.target.value)}
          className="bg-transparent text-amber-glow border-none outline-none font-mono text-[12px] cursor-pointer uppercase"
        >
          {opsForKind.map((op) => (
            <option key={op} value={op} className="bg-ink-700 text-paper">
              {OP_LABELS[op] ?? op}
            </option>
          ))}
        </select>
      </Pill>
      <LeafValueInput spec={spec} op={node.op} value={node.value} onChange={onChange} node={node} />
    </div>
  )
}

function LeafValueInput({
  spec, op, value, node, onChange,
}: {
  spec: FieldSpec
  op: string
  value: Leaf['value']
  node: Leaf
  onChange: (n: Leaf) => void
}) {
  if (NULLARY_OPS.has(op)) return null
  const set = (v: Leaf['value']) => onChange({ ...node, value: v })
  const isList = LIST_OPS.has(op)

  // ---- Enum field → dropdown(s) populated with known values ----
  if (spec.kind === 'enum' && spec.options) {
    if (isList) {
      const selected = Array.isArray(value) ? value.map(String) : []
      return (
        <Pill>
          <select
            multiple
            value={selected}
            onChange={(e) => {
              const picked = Array.from(e.target.selectedOptions).map((o) => o.value)
              set(picked)
            }}
            className="bg-transparent text-paper border-none outline-none font-mono text-[12px] min-w-[180px] max-h-[120px]"
            size={Math.min(spec.options.length, 5)}
          >
            {spec.options.map((o) => (
              <option key={o.value} value={o.value} className="bg-ink-700 text-paper">
                {o.label}
              </option>
            ))}
          </select>
        </Pill>
      )
    }
    const current = typeof value === 'string' ? value : spec.options[0].value
    return (
      <Pill>
        <select
          value={current}
          onChange={(e) => set(e.target.value)}
          className="bg-transparent text-paper border-none outline-none font-mono text-[12px] cursor-pointer"
        >
          {spec.options.map((o) => (
            <option key={o.value} value={o.value} className="bg-ink-700 text-paper">
              {o.label}
            </option>
          ))}
        </select>
      </Pill>
    )
  }

  // ---- Boolean → true/false dropdown ----
  if (spec.kind === 'boolean') {
    const current = value === true ? 'true' : value === false ? 'false' : 'true'
    return (
      <Pill>
        <select
          value={current}
          onChange={(e) => set(e.target.value === 'true')}
          className="bg-transparent text-paper border-none outline-none font-mono text-[12px] cursor-pointer"
        >
          <option value="true" className="bg-ink-700 text-paper">true</option>
          <option value="false" className="bg-ink-700 text-paper">false</option>
        </select>
      </Pill>
    )
  }

  // ---- List op on non-enum field → comma-list text input ----
  if (isList) {
    const raw = Array.isArray(value) ? value.join(', ') : String(value ?? '')
    return (
      <Pill>
        <input
          value={raw}
          onChange={(e) =>
            set(e.target.value.split(',').map((v) => v.trim()).filter(Boolean))
          }
          className="bg-transparent text-paper border-none outline-none font-mono text-[12px] w-[220px]"
          placeholder="value, value, value"
        />
      </Pill>
    )
  }

  // ---- Numeric → type=number ----
  if (spec.kind === 'numeric') {
    return (
      <Pill>
        <input
          type="number"
          value={value === null || value === undefined ? '' : String(value)}
          onChange={(e) => {
            const s = e.target.value
            if (s === '') set('')
            else set(Number(s))
          }}
          className="bg-transparent text-paper border-none outline-none font-mono text-[12px] w-[120px]"
          placeholder="0"
        />
      </Pill>
    )
  }

  // ---- Date + "in_month" → native month picker (YYYY-MM) ----
  if (spec.kind === 'date' && op === 'in_month') {
    return (
      <Pill>
        <input
          type="month"
          value={String(value ?? '')}
          onChange={(e) => set(e.target.value)}
          className="bg-transparent text-paper border-none outline-none font-mono text-[12px] w-[150px]"
          placeholder="YYYY-MM"
        />
      </Pill>
    )
  }

  // ---- Date → text with refined placeholder (supports now±Nmo / now±Nd etc.) ----
  if (spec.kind === 'date') {
    return (
      <Pill>
        <input
          value={String(value ?? '')}
          onChange={(e) => set(e.target.value)}
          className="bg-transparent text-paper border-none outline-none font-mono text-[12px] w-[200px]"
          placeholder="YYYY-MM-DD  ·  now+30d  ·  now-3mo"
        />
      </Pill>
    )
  }

  // ---- Default text input ----
  return (
    <Pill>
      <input
        value={String(value ?? '')}
        onChange={(e) => set(e.target.value)}
        className="bg-transparent text-paper border-none outline-none font-mono text-[12px] w-[200px]"
        placeholder="value"
      />
    </Pill>
  )
}

function defaultValueForKind(spec: FieldSpec, op: string): Leaf['value'] {
  if (NULLARY_OPS.has(op)) return null
  if (LIST_OPS.has(op)) return []
  if (spec.kind === 'boolean') return true
  if (spec.kind === 'enum') return spec.options?.[0]?.value ?? ''
  if (spec.kind === 'numeric') return ''
  return ''
}

function Pill({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'neutral' | 'amber' }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 border ${
        tone === 'amber' ? 'border-amber-glow/40 bg-amber-wash' : 'border-ink-500'
      }`}
    >
      {children}
    </span>
  )
}

function isComposite(n: Node): n is Composite {
  return typeof n === 'object' && n !== null && ('all' in n || 'any' in n)
}

function isEngaged(n: Node): n is Engaged {
  return typeof n === 'object' && n !== null && 'engaged' in n
}
